import { motion } from "framer-motion";

/* ── Apple easing (array format for Framer Motion) ──────────── */
export const appleEase = [0.16, 1, 0.3, 1];

/* ── Viewport config ─────────────────────────────────────────── */
export const viewportConfig = { once: true, margin: "-60px" };

/* ── Variants ────────────────────────────────────────────────── */
export const fadeUp = {
  hidden:  { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.85, ease: appleEase } },
};

/* Fade-up with blur — Apple cinematic headline reveal */
export const fadeUpBlur = {
  hidden:  { opacity: 0, y: 48, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.95, ease: appleEase } },
};

/* Scale + fade — cards, images entering viewport */
export const revealScale = {
  hidden:  { opacity: 0, scale: 0.96, filter: "blur(4px)" },
  visible: { opacity: 1, scale: 1,    filter: "blur(0px)", transition: { duration: 1.0, ease: appleEase } },
};

export const fadeIn = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8, ease: appleEase } },
};

export const scaleUp = {
  hidden:  { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1,   transition: { duration: 0.9, ease: appleEase } },
};

export const staggerContainer = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.05 } },
};

export const staggerItem = {
  hidden:  { opacity: 0, y: 30, filter: "blur(4px)" },
  visible: { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.8, ease: appleEase } },
};

export const pageTransition = {
  initial:  { opacity: 0, y: 18 },
  animate:  { opacity: 1, y: 0,   transition: { duration: 0.55, ease: appleEase } },
  exit:     { opacity: 0, y: -10, transition: { duration: 0.3,  ease: appleEase } },
};

/* ── Page wrapper — used in PageLayout with AnimatePresence ──── */
export function PageTransition({ children, style, className }) {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageTransition}
      style={style}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Section reveal — whileInView fadeUp ─────────────────────── */
export function RevealSection({ children, style, className, delay = 0, as = "section" }) {
  const Tag = as === "div" ? motion.div : motion.section;
  return (
    <Tag
      initial="hidden"
      whileInView="visible"
      viewport={viewportConfig}
      variants={{
        hidden:  fadeUp.hidden,
        visible: { ...fadeUp.visible, transition: { duration: 0.85, ease: appleEase, delay } },
      }}
      style={style}
      className={className}
    >
      {children}
    </Tag>
  );
}

/* ── Stagger grid — wraps a card grid ───────────────────────── */
export function StaggerGrid({ children, style, className }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={viewportConfig}
      variants={staggerContainer}
      style={style}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Stagger item — each card inside a StaggerGrid ──────────── */
export function StaggerItem({ children, style, className }) {
  return (
    <motion.div variants={staggerItem} style={style} className={className}>
      {children}
    </motion.div>
  );
}
